import type { GetDraftLessonActivitiesQuery } from '../lesson-builder.graphql.generated';
import ActivityBuilderIntroductionPrint from '../introduction/activity-builder-intro-print';
import { ActivityBuilderThemeContextProvider } from '../theme/activity-builder-theme';
import { INTRODUCTION_INDEX, Orientation } from '../activity-builder.apollo';
import { groupSections } from './activity-builder-a4.utils';
import * as Styled from './activity-builder-a4.style';
import { GlobalStyles } from './activity-builder-print-generator.style';
import type { InteractiveLessonSectionFragment } from '../lesson-builder.graphql.generated';
import ActivityBuilderPrintGeneratorPage from './activity-builder-print-generator-page';
import { LanguageCodes } from 'global/constants';
import type { Pluck } from 'lib/gql/pluck';

export const ActivityBuilderPrintGenerator = ({
  interactiveLesson,
  sections,
  slidesPerPage,
  orientation,
  slides,
  version,
  language,
  onRenderComplete,
}: {
  interactiveLesson: Pluck<
    GetDraftLessonActivitiesQuery,
    'getDraftLessonActivities'
  >;
  sections: InteractiveLessonSectionFragment[];
  slidesPerPage: number;
  orientation: Orientation;
  slides: number[];
  version: string | undefined;
  language: LanguageCodes;
  onRenderComplete?: (totalPages: number) => void;
}) => {
  const { sortedSections, totalPages } = groupSections({
    sections,
    slidesToDisplay: slides,
    slidesPerPage,
    isSinglePage: false,
    currentPage: undefined,
  });

  const showCover = slides.includes(INTRODUCTION_INDEX);

  return (
    <ActivityBuilderThemeContextProvider
      initialFontSize={interactiveLesson.styles?.fontSize}
    >
      <GlobalStyles $language={language}>
        <Styled.A4 $orientation={orientation}>
          {showCover && (
            <Styled.A4Page $orientation={orientation}>
              <ActivityBuilderIntroductionPrint
                interactiveLesson={interactiveLesson}
                language={language}
              />
            </Styled.A4Page>
          )}
          {sortedSections.map((group, index) => {
            return (
              <Styled.A4Page $orientation={orientation} key={index}>
                <Styled.A4Slides>
                  {group.map(section => {
                    return (
                      <ActivityBuilderPrintGeneratorPage
                        key={section.id}
                        includesIntro={showCover}
                        language={language}
                        index={index}
                        section={section}
                        version={version}
                        slidesPerPage={slidesPerPage}
                        orientation={orientation}
                        unitName={interactiveLesson.lesson.unit.name}
                        contentId={interactiveLesson.id}
                        contentName={interactiveLesson.lesson.name}
                        subject={interactiveLesson.lesson.subjects?.[0]?.label}
                        sectionsOverview={interactiveLesson?.sectionsOverview}
                        onRenderComplete={() => onRenderComplete?.(totalPages)}
                      />
                    );
                  })}
                </Styled.A4Slides>
              </Styled.A4Page>
            );
          })}
        </Styled.A4>
      </GlobalStyles>
    </ActivityBuilderThemeContextProvider>
  );
};
